import React, { useEffect, useMemo, useRef, useState } from "react";
import { CircularProgress, makeStyles, useTheme } from "@seahorse/ui";
import { fetchRealtimeSentiment } from "../Utils/CoTraderUtils";
import {
  formatChartAxisTime,
  formatShortTimeWithZone,
  formatTimeWithZone,
  getHighchartsTimeOptions,
} from "../Utils/timezone";
import { useDisplayTimeZone } from "../Utils/displayTimeZone";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";

interface RealtimeSentimentProps {
  currencyPair: string;
  updateSpeed: number;
  userLanguage: string;
  useStreamData?: boolean;
  streamSentiment?: number | null;
  streamTimestamp?: number | null;
  streamLoading?: boolean;
  compact?: boolean;
}

type SentimentPoint = {
  sentiment: number;
  timestamp: number;
};

const POSITIVE_COLOR = "#35C48B";
const NEGATIVE_COLOR = "#E25B5B";
const NEUTRAL_COLOR = "#43CFE6";
const MAX_POINTS = 24;
// Below this card width the scale drops to its own row under score + status.
// Below this card width the strip tightens its spacing instead of wrapping.
const COMPACT_TIGHT_WIDTH = 560;

const clampSentiment = (value: number) => Math.max(-1, Math.min(1, value));

// Five buckets shared by the status pill and the scale marker color.
const formatSentimentLabel = (value: number | null) => {
  if (value == null) return "Loading...";
  if (Math.abs(value) < 0.05) return "Neutral";
  const direction = value > 0 ? "Bullish" : "Bearish";
  return Math.abs(value) < 0.35
    ? `Slightly ${direction.toLowerCase()}`
    : direction;
};

const getSentimentColor = (value: number | null) => {
  if (value == null) return NEUTRAL_COLOR;
  if (value > 0.05) return POSITIVE_COLOR;
  if (value < -0.05) return NEGATIVE_COLOR;
  return NEUTRAL_COLOR;
};

const CoTraderRealtimeSentimentComponent: React.FunctionComponent<
  RealtimeSentimentProps
> = ({
  currencyPair,
  updateSpeed,
  userLanguage,
  useStreamData = false,
  streamSentiment = null,
  streamTimestamp = null,
  streamLoading = false,
  compact = false,
}) => {
  const theme = useTheme();
  const { timeZone } = useDisplayTimeZone();
  const [history, setHistory] = useState<SentimentPoint[]>([]);
  const [currentSentiment, setCurrentSentiment] = useState<number | null>(null);
  const [lastUpdatedAt, setLastUpdatedAt] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const lastProcessedStreamTimestampRef = useRef<number | null>(null);
  // Keeps the last non-zero change so the chip can fade out without
  // its text collapsing mid-transition.
  const lastVisibleDeltaRef = useRef<number | null>(null);
  const compactCardRef = useRef<HTMLDivElement | null>(null);
  const [isCompactTight, setIsCompactTight] = useState(false);

  useEffect(() => {
    const node = compactCardRef.current;
    if (!compact || !node || typeof ResizeObserver === "undefined") {
      return undefined;
    }

    const resizeObserver = new ResizeObserver((entries) => {
      const width = entries[0]?.contentRect.width ?? node.clientWidth;
      setIsCompactTight(width > 0 && width < COMPACT_TIGHT_WIDTH);
    });
    resizeObserver.observe(node);

    return () => resizeObserver.disconnect();
  }, [compact]);

  const colors = {
    background: theme.globallink.colors.Grey[1100],
    border: theme.globallink.colors.Slate[700],
    textPrimary: theme.globallink.colors.White[500],
    textSecondary: theme.globallink.colors.Grey[500],
    textMuted: theme.globallink.colors.Grey[600],
    surface: "rgba(255,255,255,0.03)",
  };

  const useStyles = makeStyles({
    container: {
      width: "100%",
      height: "100%",
      minHeight: 0,
      display: "flex",
      flexDirection: "column",
      gap: "12px",
      padding: "14px",
      backgroundColor: colors.background,
      overflow: "hidden",
    },
    loadingState: {
      flex: 1,
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
    },
    emptyState: {
      flex: 1,
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
      alignItems: "center",
      textAlign: "center",
      gap: "8px",
      color: colors.textSecondary,
      padding: "16px",
    },
    metricRow: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "flex-start",
      gap: "12px",
      flexWrap: "wrap",
    },
    scoreBlock: {
      display: "flex",
      flexDirection: "column",
      gap: "2px",
      minWidth: 0,
    },
    scoreLabel: {
      fontSize: "11px",
      textTransform: "uppercase",
      letterSpacing: "0.08em",
      color: colors.textMuted,
      fontWeight: 700,
    },
    scoreValue: {
      fontSize: "34px",
      lineHeight: "38px",
      fontWeight: 800,
      color: colors.textPrimary,
    },
    scoreMeta: {
      fontSize: "12px",
      color: colors.textSecondary,
    },
    statusPill: {
      borderRadius: "999px",
      padding: "8px 12px",
      fontSize: "12px",
      fontWeight: 700,
      color: "#FFFFFF",
      alignSelf: "flex-start",
    },
    scaleWrap: {
      display: "flex",
      flexDirection: "column",
      gap: "6px",
    },
    scaleTrack: {
      position: "relative",
      height: "12px",
      borderRadius: "999px",
      overflow: "hidden",
      background:
        "linear-gradient(90deg, rgba(226,91,91,0.32) 0%, rgba(67,207,230,0.22) 50%, rgba(53,196,139,0.32) 100%)",
      border: `1px solid ${colors.border}`,
    },
    scaleCenterLine: {
      position: "absolute",
      top: 0,
      bottom: 0,
      left: "50%",
      width: "1px",
      background: "rgba(255,255,255,0.4)",
      transform: "translateX(-50%)",
    },
    scaleIndicator: {
      position: "absolute",
      top: "-3px",
      width: "18px",
      height: "18px",
      borderRadius: "50%",
      border: "2px solid rgba(7,12,18,0.85)",
      boxShadow: "0 0 0 2px rgba(255,255,255,0.08)",
      transform: "translateX(-50%)",
    },
    scaleLabels: {
      display: "flex",
      justifyContent: "space-between",
      fontSize: "11px",
      color: colors.textMuted,
    },
    chartWrap: {
      flex: 1,
      minHeight: "130px",
      borderRadius: "10px",
      border: `1px solid ${colors.border}`,
      background: colors.surface,
      padding: "12px",
      display: "flex",
      flexDirection: "column",
      gap: "8px",
    },
    chartHeader: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      gap: "8px",
      fontSize: "12px",
      color: colors.textSecondary,
      flexWrap: "wrap",
    },
    chartTitle: {
      color: colors.textPrimary,
      fontWeight: 700,
    },
    chartCanvas: {
      flex: 1,
      minHeight: 0,
    },
    chartAxisRow: {
      display: "flex",
      justifyContent: "space-between",
      fontSize: "11px",
      color: colors.textMuted,
    },
    compactCard: {
      width: "100%",
      display: "flex",
      alignItems: "center",
      flexWrap: "nowrap",
      minWidth: 0,
      overflow: "hidden",
      padding: "12px 18px",
      borderRadius: "10px",
      backgroundColor: colors.background,
      border: "1px solid rgba(255,255,255,0.08)",
      boxShadow: "inset 0 1px 0 rgba(255,255,255,0.03)",
    },
    compactScoreBlock: {
      display: "flex",
      flexDirection: "column",
      gap: "3px",
      flexShrink: 0,
      minWidth: "120px",
      paddingRight: "20px",
    },
    compactLabel: {
      fontSize: "11px",
      lineHeight: "14px",
      letterSpacing: "0.02em",
      color: colors.textMuted,
      fontWeight: 500,
    },
    compactValueRow: {
      position: "relative",
      display: "inline-flex",
      alignSelf: "flex-start",
      alignItems: "center",
    },
    compactValue: {
      fontSize: "20px",
      lineHeight: "24px",
      fontWeight: 600,
      letterSpacing: "-0.01em",
      color: "rgba(255,255,255,0.94)",
      fontVariantNumeric: "tabular-nums",
    },
    // Absolutely positioned so showing/hiding the chip never reflows the strip.
    compactTrend: {
      position: "absolute",
      top: "50%",
      left: "calc(100% + 6px)",
      display: "inline-flex",
      alignItems: "center",
      gap: "3px",
      padding: "1px 5px 1px 4px",
      borderRadius: "4px",
      fontSize: "10px",
      lineHeight: "14px",
      fontWeight: 600,
      fontVariantNumeric: "tabular-nums",
      whiteSpace: "nowrap",
      transition: "opacity 0.25s ease, transform 0.25s ease",
    },
    compactScale: {
      flex: "1 1 0",
      minWidth: 0,
      display: "flex",
      flexDirection: "column",
      gap: "8px",
      padding: "4px 24px 0",
    },
    compactScaleTrack: {
      position: "relative",
      height: "4px",
      borderRadius: "999px",
      background:
        "linear-gradient(90deg, rgba(226,91,91,0.7) 0%, rgba(226,91,91,0.25) 35%, rgba(255,255,255,0.12) 50%, rgba(53,196,139,0.25) 65%, rgba(53,196,139,0.7) 100%)",
    },
    compactScaleCenterLine: {
      position: "absolute",
      top: "-3px",
      bottom: "-3px",
      left: "50%",
      width: "1px",
      background: "rgba(255,255,255,0.28)",
      transform: "translateX(-50%)",
    },
    compactScaleIndicator: {
      position: "absolute",
      top: "50%",
      width: "12px",
      height: "12px",
      borderRadius: "50%",
      border: "2px solid rgba(12,17,24,0.95)",
      transform: "translate(-50%, -50%)",
      transition: "left 0.4s ease",
    },
    compactScaleTicks: {
      display: "flex",
      justifyContent: "space-between",
      gap: "6px",
      whiteSpace: "nowrap",
      fontSize: "10.5px",
      lineHeight: "14px",
      color: colors.textMuted,
      fontVariantNumeric: "tabular-nums",
    },
    compactScaleTickValue: {
      opacity: 0.6,
      marginLeft: "4px",
    },
    compactStatus: {
      display: "flex",
      flexDirection: "column",
      alignItems: "flex-end",
      gap: "5px",
      marginLeft: "auto",
      paddingLeft: "20px",
      flexShrink: 0,
      whiteSpace: "nowrap",
    },
    compactStatusPill: {
      display: "inline-flex",
      alignItems: "center",
      gap: "7px",
      padding: "4px 12px 4px 10px",
      borderRadius: "8px",
      border: "1px solid",
      fontSize: "12px",
      lineHeight: "16px",
      fontWeight: 600,
      letterSpacing: "0.01em",
      transition: "color 0.3s ease, background 0.3s ease, border-color 0.3s ease",
    },
    compactStatusDot: {
      width: "6px",
      height: "6px",
      borderRadius: "50%",
    },
    compactMeta: {
      maxWidth: "100%",
      overflow: "hidden",
      textOverflow: "ellipsis",
      fontSize: "10.5px",
      lineHeight: "14px",
      color: colors.textMuted,
    },
    // Narrow-card overrides; must stay after the base compact* rules.
    // The strip stays on one row and the scale absorbs the lost width.
    compactTightCard: {
      padding: "10px 12px",
    },
    compactTightScoreBlock: {
      minWidth: 0,
      paddingRight: "12px",
    },
    compactTightScale: {
      padding: "4px 8px 0",
    },
    compactTightTickValue: {
      display: "none",
    },
    compactTightStatus: {
      flexShrink: 1,
      minWidth: 0,
      paddingLeft: "8px",
    },
  });

  const classes = useStyles();

  useEffect(() => {
    if (useStreamData) return undefined;

    let isMounted = true;

    const updateSentiment = async () => {
      try {
        const nextSentiment = await fetchRealtimeSentiment({
          currencyPair,
          forward_tile: false,
          userLanguage,
        });

        if (!isMounted) return;

        setError(null);
        setLoading(false);
        setLastUpdatedAt(Date.now());
        setCurrentSentiment(nextSentiment.sentiment);

        if (nextSentiment.sentiment == null) {
          return;
        }

        const nextSentimentValue = clampSentiment(nextSentiment.sentiment);

        setHistory((prev) => {
          const nextPoint = {
            sentiment: nextSentimentValue,
            timestamp: Date.now(),
          };

          return [...prev, nextPoint].slice(-MAX_POINTS);
        });
      } catch (nextError) {
        if (!isMounted) return;
        setLoading(false);
        setError("Unable to load sentiment right now.");
      }
    };

    setHistory([]);
    setCurrentSentiment(null);
    setLastUpdatedAt(null);
    setLoading(true);
    setError(null);

    updateSentiment();

    const intervalId = setInterval(() => {
      updateSentiment();
    }, updateSpeed * 10000);

    return () => {
      isMounted = false;
      clearInterval(intervalId);
    };
  }, [currencyPair, updateSpeed, useStreamData, userLanguage]);

  useEffect(() => {
    if (!useStreamData) return;

    setHistory([]);
    setCurrentSentiment(null);
    setLastUpdatedAt(null);
    setError(null);
    lastProcessedStreamTimestampRef.current = null;
  }, [currencyPair, useStreamData]);

  useEffect(() => {
    if (!useStreamData) return;

    setLoading(streamLoading && streamTimestamp == null);

    if (streamTimestamp == null) return;
    if (lastProcessedStreamTimestampRef.current === streamTimestamp) return;

    lastProcessedStreamTimestampRef.current = streamTimestamp;
    setError(null);
    setLastUpdatedAt(streamTimestamp);
    setCurrentSentiment(streamSentiment ?? null);
    setLoading(false);

    if (streamSentiment == null) {
      return;
    }

    const nextSentimentValue = clampSentiment(streamSentiment);
    setHistory((prev) => {
      const nextPoint = {
        sentiment: nextSentimentValue,
        timestamp: streamTimestamp,
      };

      return [...prev, nextPoint].slice(-MAX_POINTS);
    });
  }, [streamLoading, streamSentiment, streamTimestamp, useStreamData]);

  const latestRenderableSentiment =
    currentSentiment ?? history[history.length - 1]?.sentiment ?? null;
  const sentimentColor = getSentimentColor(latestRenderableSentiment);
  const sentimentLabel = formatSentimentLabel(latestRenderableSentiment);
  const indicatorPosition =
    latestRenderableSentiment == null
      ? 50
      : ((clampSentiment(latestRenderableSentiment) + 1) / 2) * 100;
  const summaryMeta =
    error && latestRenderableSentiment == null && history.length === 0
      ? error
      : lastUpdatedAt == null
        ? loading
          ? "Loading live sentiment"
          : "Waiting for live refresh"
        : `Last updated ${formatTimeWithZone(lastUpdatedAt, timeZone)}`;
  const compactSummaryMeta =
    error && latestRenderableSentiment == null && history.length === 0
      ? "Unavailable"
      : lastUpdatedAt == null
        ? loading
          ? "Fetching live sentiment..."
          : "Waiting"
        : `Updated ${formatShortTimeWithZone(lastUpdatedAt, timeZone)}`;
  const sentimentChartOptions = useMemo(
    () => ({
      time: getHighchartsTimeOptions(timeZone),
      chart: {
        type: "line",
        backgroundColor: "transparent",
        spacing: [8, 0, 8, 0],
        margin: [10, 8, 24, 36],
        animation: false,
      },
      title: {
        text: "",
      },
      xAxis: {
        type: "datetime",
        lineColor: "rgba(255,255,255,0.14)",
        tickColor: "rgba(255,255,255,0.14)",
        labels: {
          style: {
            color: colors.textMuted,
            fontSize: "10px",
          },
          formatter: function (this: Highcharts.AxisLabelsFormatterContextObject) {
            return formatChartAxisTime(Number(this.value), timeZone);
          },
        },
      },
      yAxis: {
        title: {
          text: "",
        },
        min: -1,
        max: 1,
        tickPositions: [-1, -0.5, 0, 0.5, 1],
        gridLineColor: "rgba(255,255,255,0.08)",
        lineColor: "rgba(255,255,255,0.14)",
        labels: {
          style: {
            color: colors.textMuted,
            fontSize: "10px",
          },
          formatter: function (this: Highcharts.AxisLabelsFormatterContextObject) {
            return Number(this.value).toFixed(1);
          },
        },
      },
      legend: {
        enabled: false,
      },
      credits: {
        enabled: false,
      },
      tooltip: {
        backgroundColor: theme.globallink.colors.DarkGrey[500],
        borderColor: "rgba(255,255,255,0.12)",
        style: {
          color: theme.globallink.colors.White[500],
        },
        formatter: function (this: Highcharts.TooltipFormatterContextObject) {
          return `${formatTimeWithZone(Number(this.x), timeZone)}<br/><b>${Number(
            this.y
          ).toFixed(2)}</b>`;
        },
      },
      plotOptions: {
        series: {
          animation: false,
          marker: {
            enabled: true,
            radius: 2.5,
            fillColor: sentimentColor,
            lineColor: sentimentColor,
          },
          states: {
            hover: {
              lineWidthPlus: 0,
            },
          },
        },
      },
      series: [
        {
          type: "line" as const,
          data: history.map((point) => [point.timestamp, point.sentiment]),
          color: sentimentColor,
          lineWidth: 2.5,
          name: "Sentiment",
        },
      ],
    }),
    [colors.textMuted, history, sentimentColor, theme, timeZone]
  );

  const sentimentSummary = (
    <>
      <div className={classes.metricRow}>
        <div className={classes.scoreBlock}>
          <div className={classes.scoreLabel}>Current Sentiment</div>
          <div
            className={classes.scoreValue}
            style={
              compact
                ? {
                    fontSize: "26px",
                    lineHeight: "30px",
                  }
                : undefined
            }
          >
            {latestRenderableSentiment == null
              ? "--"
              : latestRenderableSentiment.toFixed(2)}
          </div>
          <div className={classes.scoreMeta}>{summaryMeta}</div>
        </div>
        <div
          className={classes.statusPill}
          style={{ backgroundColor: sentimentColor }}
        >
          {sentimentLabel}
        </div>
      </div>

      <div className={classes.scaleWrap}>
        <div className={classes.scaleTrack}>
          <div className={classes.scaleCenterLine} />
          <div
            className={classes.scaleIndicator}
            style={{
              left: `${indicatorPosition}%`,
              backgroundColor: sentimentColor,
            }}
          />
        </div>
        <div className={classes.scaleLabels}>
          <span>-1.0 Bearish</span>
          <span>0.0 Neutral</span>
          <span>1.0 Bullish</span>
        </div>
      </div>
    </>
  );

  const previousSentiment =
    history.length > 1 ? history[history.length - 2].sentiment : null;
  const sentimentDelta =
    latestRenderableSentiment == null || previousSentiment == null
      ? null
      : latestRenderableSentiment - previousSentiment;
  const deltaDirection =
    sentimentDelta == null || Math.abs(sentimentDelta) < 0.005
      ? "flat"
      : sentimentDelta > 0
        ? "up"
        : "down";
  if (sentimentDelta != null && deltaDirection !== "flat") {
    lastVisibleDeltaRef.current = sentimentDelta;
  }
  const isDeltaVisible = deltaDirection !== "flat";
  const displayedDelta = lastVisibleDeltaRef.current;
  const deltaColor =
    displayedDelta != null && displayedDelta < 0
      ? NEGATIVE_COLOR
      : POSITIVE_COLOR;
  const isStrongSentiment =
    latestRenderableSentiment != null &&
    Math.abs(latestRenderableSentiment) >= 0.35;
  const isNeutralSentiment =
    latestRenderableSentiment == null ||
    Math.abs(latestRenderableSentiment) < 0.05;
  const statusPillStyle = isNeutralSentiment
    ? {
        color: "rgba(255,255,255,0.86)",
        background: "rgba(255,255,255,0.04)",
        borderColor: "rgba(255,255,255,0.14)",
      }
    : {
        color: sentimentColor,
        background: `${sentimentColor}${isStrongSentiment ? "24" : "14"}`,
        borderColor: `${sentimentColor}${isStrongSentiment ? "66" : "3D"}`,
      };
  const tickValueClassName = `${classes.compactScaleTickValue} ${
    isCompactTight ? classes.compactTightTickValue : ""
  }`;
  const compactSentimentSummary = (
    <>
      <div
        className={`${classes.compactScoreBlock} ${
          isCompactTight ? classes.compactTightScoreBlock : ""
        }`}
      >
        <span className={classes.compactLabel}>Market Sentiment</span>
        <div className={classes.compactValueRow}>
          <span className={classes.compactValue}>
            {latestRenderableSentiment == null
              ? "--"
              : latestRenderableSentiment.toFixed(2)}
          </span>
          <span
            className={classes.compactTrend}
            title={isDeltaVisible ? "Change since previous update" : undefined}
            aria-hidden={!isDeltaVisible}
            style={{
              color: deltaColor,
              background: `${deltaColor}1A`,
              opacity: isDeltaVisible ? 1 : 0,
              transform: isDeltaVisible
                ? "translateY(-50%)"
                : "translateY(calc(-50% + 2px))",
              pointerEvents: isDeltaVisible ? undefined : "none",
            }}
          >
            <svg
              width="7"
              height="7"
              viewBox="0 0 10 10"
              aria-hidden="true"
              style={{
                transform:
                  displayedDelta != null && displayedDelta < 0
                    ? "rotate(180deg)"
                    : undefined,
              }}
            >
              <path d="M5 1.5 9 7.5H1z" fill="currentColor" />
            </svg>
            {displayedDelta == null
              ? ""
              : `${displayedDelta > 0 ? "+" : ""}${displayedDelta.toFixed(2)}`}
          </span>
        </div>
      </div>

      <div
        className={`${classes.compactScale} ${
          isCompactTight ? classes.compactTightScale : ""
        }`}
        aria-hidden="true"
      >
        <div className={classes.compactScaleTrack}>
          <div className={classes.compactScaleCenterLine} />
          <div
            className={classes.compactScaleIndicator}
            style={{
              left: `${indicatorPosition}%`,
              backgroundColor: sentimentColor,
              boxShadow: `0 0 0 4px ${sentimentColor}26, 0 0 10px ${sentimentColor}66`,
            }}
          />
        </div>
        <div className={classes.compactScaleTicks}>
          <span>
            Bearish<span className={tickValueClassName}>-1</span>
          </span>
          <span>
            Neutral<span className={tickValueClassName}>0</span>
          </span>
          <span>
            Bullish<span className={tickValueClassName}>+1</span>
          </span>
        </div>
      </div>

      <div
        className={`${classes.compactStatus} ${
          isCompactTight ? classes.compactTightStatus : ""
        }`}
      >
        <span className={classes.compactStatusPill} style={statusPillStyle}>
          <span
            className={classes.compactStatusDot}
            style={{
              backgroundColor: isNeutralSentiment
                ? NEUTRAL_COLOR
                : sentimentColor,
              boxShadow: isStrongSentiment
                ? `0 0 6px ${sentimentColor}`
                : "none",
            }}
          />
          {sentimentLabel}
        </span>
        <span className={classes.compactMeta}>{compactSummaryMeta}</span>
      </div>
    </>
  );

  if (compact) {
    return (
      <div
        className={`${classes.compactCard} ${
          isCompactTight ? classes.compactTightCard : ""
        }`}
        ref={compactCardRef}
      >
        {compactSentimentSummary}
      </div>
    );
  }

  if (loading) {
    return (
      <div className={classes.container}>
        <div className={classes.loadingState}>
          <CircularProgress />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={classes.container}>
        <div className={classes.emptyState}>
          <div>{error}</div>
          <div>We will retry automatically using the live updates feed.</div>
        </div>
      </div>
    );
  }

  if (latestRenderableSentiment == null && history.length === 0) {
    return (
      <div className={classes.container}>
        <div className={classes.emptyState}>
          <div>Realtime sentiment is not available yet.</div>
          <div>
            The backend is returning `null` for this symbol at the moment, so no
            chart points have been plotted yet.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={classes.container}>
      {sentimentSummary}

      <div className={classes.chartWrap}>
        <div className={classes.chartHeader}>
          <span className={classes.chartTitle}>Sentiment Trend</span>
          <span>Rolling history for {currencyPair}</span>
        </div>
        <div className={classes.chartCanvas}>
          <HighchartsReact
            // Highcharts 11 ignores `time` changes on update; remount instead.
            key={timeZone}
            highcharts={Highcharts}
            options={sentimentChartOptions}
          />
        </div>
        <div className={classes.chartAxisRow}>
          <span>Oldest</span>
          <span>Latest</span>
        </div>
      </div>
    </div>
  );
};

export default CoTraderRealtimeSentimentComponent;
